import routes from "@/constants/routes";
import RightArrow from "@/public/icons/hiw-right-arrow.svg";
import Image from "next/image";
import { useState, useRef } from "react";

import { DotLottie } from "@lottiefiles/dotlottie-web";

const Hero = () => {
  const [isAnimationLoaded, setIsAnimationLoaded] = useState(false);
  const [isMobileAnimationLoaded, setIsMobileAnimationLoaded] = useState(false);
  const [isStarsHovered, setIsStarsHovered] = useState(false);
  const animationRef = useRef<DotLottie | null>(null);
  const mobileAnimationRef = useRef<DotLottie | null>(null);
  const isLoadedRef = useRef(false);
  const isMobileLoadedRef = useRef(false);

  return (
    <>
      <div className="flex flex-col bg-landing-blue md:flex-row w-full pb-22 py-8 md:py-16 text-white relative md:px-6 md:pr-0 px-5 lg:px-11 xl:px-32 xl:pt-30 xl:pb-22 h-fit justify-between">
        <div className="w-full md:w-auto lg:w-full h-auto z-10">
          <h1>
            <span className="text-3xl font-bold md:text-5xl xl:text-[76px] text-landing-white">
              Rent now
            </span>
            <span className="block xl:mb-2 text-3xl font-bold md:text-5xl whitespace-nowrap text-landing-orange xl:text-[76px]">
              Buy later
            </span>
            <span className="text-3xl font-bold md:text-5xl xl:text-[76px] text-landing-white">
              Rent returned
            </span>
          </h1>

          <div className="md:hidden block relative h-[220px] top-[-1px] overflow-hidden z-5">
            {!isMobileAnimationLoaded && (
              <div className="absolute inset-0">
                <Image
                  src="/now/keyzy-now-hero-banner.svg"
                  alt="Hero image"
                  objectFit="fill"
                  priority
                  layout="fill"
                />
              </div>
            )}
            <canvas
              ref={(el) => {
                if (el && !mobileAnimationRef.current) {
                  const dotLottie = new DotLottie({
                    canvas: el,
                    src: "/landing/hero-animation.lottie",
                    autoplay: true,
                    loop: true,
                    renderConfig: {
                      autoResize: true,
                    },
                  });
                  mobileAnimationRef.current = dotLottie;

                  // Try multiple event names to detect when animation is loaded
                  const handleLoad = () => {
                    if (isMobileLoadedRef.current) return;
                    isMobileLoadedRef.current = true;
                    setIsMobileAnimationLoaded(true);
                  };

                  // Try 'ready' event
                  if (typeof dotLottie.addEventListener === "function") {
                    dotLottie.addEventListener("ready", handleLoad);
                    dotLottie.addEventListener("load", handleLoad);
                    dotLottie.addEventListener("play", handleLoad);
                  }

                  // Also try checking after a short delay if events don't fire
                  setTimeout(() => {
                    if (!isMobileLoadedRef.current) {
                      handleLoad();
                    }
                  }, 500);
                }
              }}
              className={`absolute w-[100%] h-[220px] min-w-[100%] overflow-hidden z-4 ${
                !isMobileAnimationLoaded ? "opacity-0" : "opacity-100"
              } transition-opacity duration-300`}
              style={{ backgroundColor: "#272F42" }}
            />
          </div>

          <h2 className="text-landing-white font-serif text-xl max-w-xs xs:max-w-md md:max-w-none xl:text-2xl md:mt-4 xl:mt-7 md:leading-8 mt-2 md:w-[19.8rem] lg:w-[30.375rem]">
            Rent a new Keyzy home in London and buy it later with all your rent going towards your
            deposit
          </h2>

          <div className="flex flex-col items-center md:items-start gap-2">
            <div className="flex flex-col md:flex-row items-center gap-4 mt-4 xl:mt-6">
              <a
                href={routes.searchHomes}
                className="py-2.5 text-base md:text-xl md:h-auto w-60 md:w-48 p-4 text-center text-base font-bold text-white transition duration-300 ease-in-out transform rounded shadow bg-landing-orange hover:scale-105 cursor-pointer z-40"
              >
                Browse homes
              </a>
              <a
                href={routes.howItWorks}
                className="group text-base md:text-lg font-bold text-landing-orange transition duration-300 ease-in-out transform cursor-pointer z-40 relative flex items-center"
              >
                See how it works
                <RightArrow className="ml-3 transition-all duration-300 ease-in-out group-hover:ml-6" />
              </a>
            </div>
            <a
              href="https://www.trustpilot.com/review/keyzy.com"
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-3 mt-4 z-40"
              onMouseEnter={() => setIsStarsHovered(true)}
              onMouseLeave={() => setIsStarsHovered(false)}
              onClick={() => setIsStarsHovered(false)}
            >
              <span className="text-white text-base font-semibold">
                <span className="font-normal">Rated </span>
                Excellent
              </span>
              <div className="flex items-center gap-1 py-4">
                {[...Array(5)].map((_, i) => (
                  <span
                    key={i}
                    className={`inline-block transition-transform duration-200 items-center flex ${
                      isStarsHovered ? "scale-125" : "scale-100"
                    }`}
                    style={{ transitionDelay: `${i * 60}ms` }}
                  >
                    <Image
                      src={i < 4 ? "/cutomers-reviews/star.svg" : "/cutomers-reviews/half-star.svg"}
                      alt={i < 4 ? "star" : "half star"}
                      width={22}
                      height={22}
                      style={{
                        borderTopRightRadius: i < 4 ? "3px" : "3.5px",
                        borderBottomRightRadius: i < 4 ? "3px" : "3.5px",
                      }}
                      className="rounded-l-[3px] object-cover"
                    />
                  </span>
                ))}
              </div>
              <span className="flex items-center gap-1.5">
                <svg
                  width="20"
                  height="20"
                  viewBox="0 0 17.5 17.5"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M17.4841 6.35L10.8055 6.349L8.742 0L6.679 6.349L0 6.348L5.403 10.275L3.34 16.628L8.743 12.703L14.144 16.628L12.081 10.277L17.484 6.352Z"
                    fill="#23AD7A"
                  />
                  <path
                    d="M12.549 11.718L12.081 10.279L8.743 12.703L12.549 11.718Z"
                    fill="#00532C"
                  />
                </svg>
                <span className="text-white text-base font-semibold group-hover:underline">
                  Trustpilot
                </span>
              </span>
            </a>
          </div>
        </div>

        <div className="hidden md:block relative h-64 md:h-[25rem] lg:h-[25rem] xl:h-[35rem] w-full overflow-hidden mt-16 md:mt-0">
          <div className="absolute inset-0">
            {!isAnimationLoaded && (
              <div className="absolute inset-0">
                <Image
                  src="/now/keyzy-now-hero-banner.svg"
                  alt="Hero image"
                  objectFit="fill"
                  priority
                  layout="fill"
                />
              </div>
            )}
            <canvas
              ref={(el) => {
                if (el && !animationRef.current) {
                  const dotLottie = new DotLottie({
                    canvas: el,
                    src: "/landing/hero-animation.lottie",
                    autoplay: true,
                    loop: true,
                    renderConfig: {
                      autoResize: true,
                    },
                  });
                  animationRef.current = dotLottie;

                  // Try multiple event names to detect when animation is loaded
                  const handleLoad = () => {
                    if (isLoadedRef.current) return;
                    isLoadedRef.current = true;
                    setIsAnimationLoaded(true);
                  };

                  // Try 'ready' event
                  if (typeof dotLottie.addEventListener === "function") {
                    dotLottie.addEventListener("ready", handleLoad);
                    dotLottie.addEventListener("load", handleLoad);
                    dotLottie.addEventListener("play", handleLoad);
                  }

                  // Also try checking after a short delay if events don't fire
                  setTimeout(() => {
                    if (!isLoadedRef.current) {
                      handleLoad();
                    }
                  }, 500);
                }
              }}
              className={`absolute left-0 bottom-0 w-full h-full min-w-[110%] lg:bottom-[0rem] ${
                !isAnimationLoaded ? "opacity-0" : "opacity-100"
              } transition-opacity duration-300`}
              style={{ backgroundColor: "#272F42" }}
            />
          </div>
        </div>
      </div>
    </>
  );
};

export default Hero;
